import React from "react";
import ReactDOM from "react-dom/client";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ClerkProvider, useAuth } from "@clerk/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { App } from "./App";
import { SignInGate } from "./components/SignInGate";
import { PlanGate } from "./components/PlanGate";
import { DownloadView } from "./components/DownloadView";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { PostHogIdentify } from "./components/PostHogIdentify";
import { CLERK_APPEARANCE } from "./clerkAppearance";
import { convex } from "./lib/convex";
import { clerkProxyProps } from "./lib/clerkProxy";
import { initPostHog } from "./lib/posthog";
import { scheduleLaunchUpdateCheck } from "./services/updater";
import "./styles.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error("VITE_CLERK_PUBLISHABLE_KEY is not set. Add it to apps/web-react/.env.local.");
}

// Boot PostHog before React renders so the first $pageview is captured. No-op
// when VITE_POSTHOG_KEY is absent (local dev / tests). Runs in both the web
// funnel and the Tauri desktop shell — they share this bundle.
initPostHog();

// Desktop shell only (no-op everywhere else): send clerk-js's Frontend API
// traffic through the Bun host's same-origin relay, so Clerk's `__client`
// cookie is first-party and the client id stays stable. Without it, prod
// desktop sign-in cannot complete at all — see src/clerkRelay.js.
const clerkProxy = clerkProxyProps(PUBLISHABLE_KEY);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        appearance={CLERK_APPEARANCE}
        {...clerkProxy}
      >
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          {/* Syncs PostHog identity with Clerk auth (identify / reset). */}
          <PostHogIdentify />
          <AppErrorBoundary>
            {window.location.pathname.startsWith("/download") ? (
              // Public download page — no login or subscription required. Steerly
              // is free to download; the login + active-sub gate is enforced when
              // the app is OPENED (SignInGate → PlanGate below).
              <DownloadView />
            ) : (
              <SignInGate>
                <PlanGate>
                  <App />
                </PlanGate>
              </SignInGate>
            )}
          </AppErrorBoundary>
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </PostHogProvider>
  </React.StrictMode>
);

// Desktop (Tauri) only: check for an app update shortly after launch. No-op on
// the web app (app.steerly.ai). Kept outside the React tree so it fires once
// per process start, not on every re-render.
scheduleLaunchUpdateCheck();
